import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { useNavigate } from "react-router-dom";

const FEATURES = [
  "Cross-platform posting (LinkedIn, Facebook, Instagram)",
  "Content scheduling & calendar",
  "Approval workflows & team collaboration",
  "Analytics & performance insights",
  "Unlimited access to all features",
  "Priority support",
];

/**
 * SubscriptionModal — shown to "Interested" (trial) users when they
 * try to access a locked feature. Pass open/onClose to control it.
 * Optionally pass userId to forward it to the payment page.
 */
const SubscriptionModal = ({ open, onClose, userId }) => {
  const navigate = useNavigate();

  const handleViewPricing = () => {
    onClose();
    navigate("/pricing", { state: { userId } });
  };

  return (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
  >
    {/* Gradient header */}
    <Box
      sx={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        py: 3,
        px: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.5,
        }}
      >
        <LockIcon sx={{ color: "#fff", fontSize: 30 }} />
      </Box>
      <Typography
        variant="h6"
        sx={{ color: "#fff", fontWeight: 800, fontSize: "1.2rem", textAlign: "center" }}
      >
        Trial Mode Active
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "rgba(255,255,255,0.88)", textAlign: "center", fontSize: "0.88rem" }}
      >
        Upgrade to unlock full access to all features
      </Typography>
    </Box>

    <DialogContent sx={{ pt: 3, px: 3, pb: 1 }}>
      <Typography
        variant="body2"
        sx={{ color: "#64748b", mb: 2, textAlign: "center", lineHeight: 1.6 }}
      >
        Subscribe to access everything on the platform:
      </Typography>
      {FEATURES.map((feature) => (
        <Box
          key={feature}
          sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.88rem" }}>
            {feature}
          </Typography>
        </Box>
      ))}
    </DialogContent>

    <DialogActions sx={{ px: 3, pb: 3, pt: 2, flexDirection: "column", gap: 1 }}>
      <Button
        fullWidth
        variant="contained"
        startIcon={<RocketLaunchIcon />}
        onClick={handleViewPricing}
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#fff",
          fontWeight: 700,
          borderRadius: 2,
          py: 1.2,
          fontSize: "0.95rem",
          textTransform: "none",
          boxShadow: "0 4px 14px rgba(102, 126, 234, 0.4)",
          "&:hover": {
            background: "linear-gradient(135deg, #5a6fd8 0%, #6a3d92 100%)",
            transform: "translateY(-1px)",
            boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
          },
          transition: "all 0.2s ease",
        }}
      >
        View Pricing & Subscribe
      </Button>
      <Button
        fullWidth
        variant="text"
        onClick={onClose}
        sx={{
          color: "#64748b",
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 600,
          "&:hover": { color: "#764ba2", bgcolor: "rgba(118, 75, 162, 0.06)" },
        }}
      >
        Maybe Later
      </Button>
    </DialogActions>
  </Dialog>
  );
};

export default SubscriptionModal;
